import type {
  BidReviewResult,
  ContractTermAnalysis,
  FrameworkAnalysis,
  TimelineCheck,
} from "../types"

import { contractBidPositionLabelSv, statusLabelSv } from "./tender-status-label"

type BidFinding = BidReviewResult["findings"][number]

function renderFindingGroups(findings: BidFinding[]): string {
  const groups: [BidFinding[], string][] = [
    [findings.filter((f) => f.severity === "critical"), "Kritiska"],
    [findings.filter((f) => f.severity === "major"), "Stora"],
    [findings.filter((f) => f.severity === "minor"), "Mindre"],
    [findings.filter((f) => f.severity === "info"), "Info"],
  ]
  const parts: string[] = []
  for (const [group, label] of groups) {
    if (group.length === 0) continue
    parts.push(`\n**${label} fynd (${group.length})**\n`)
    for (const f of group) {
      parts.push(`- **[${f.id}] ${f.title}** *(${f.findingType})*: ${f.description}`)
      if (f.suggestedAction) parts.push(`  → ${f.suggestedAction}`)
    }
  }
  return parts.join("\n")
}

function renderContractAnalyses(analyses: ContractTermAnalysis[]): string {
  if (analyses.length === 0) return ""
  const rows = analyses
    .map(
      (a) =>
        `| ${a.termId} | ${a.clause} | ${contractBidPositionLabelSv(a.bidPosition)} | ${statusLabelSv(a.riskLevel)} |`
    )
    .join("\n")
  const lines = [
    `\n#### Avtalsvillkorsanalys\n| ID | Klausul | Anbud position | Risk |\n|---|---|---|---|\n${rows}\n`,
  ]
  for (const a of analyses) {
    if (a.deviationDescription || a.suggestedAction) {
      lines.push(`\n##### ${a.termId} — ${a.clause}`)
      if (a.deviationDescription) lines.push(`\n**Avvikelse:** ${a.deviationDescription}`)
      if (a.suggestedAction) lines.push(`\n**Åtgärd:** ${a.suggestedAction}`)
    }
  }
  return lines.join("\n")
}

function renderTimelineChecks(checks: TimelineCheck[]): string {
  if (checks.length === 0) return ""
  const rows = checks
    .map(
      (c) => `| ${c.dateId} | ${c.event} | ${c.requiredDate} | ${statusLabelSv(c.bidAlignment)} |`
    )
    .join("\n")
  return `\n#### Tidslinjeanalys\n| ID | Händelse | Krav datum | Status |\n|---|---|---|---|\n${rows}\n`
}

function renderFrameworkAnalyses(analyses: FrameworkAnalysis[]): string {
  if (analyses.length === 0) return ""
  const lines: string[] = ["\n#### Ramverksanalys\n"]
  for (const f of analyses) {
    lines.push(`**${f.frameworkName}** — ${statusLabelSv(f.overallStatus)}`)
    lines.push(`\n${f.summary}`)
    if (f.clauseChecks.length > 0) {
      const clauseRows = f.clauseChecks
        .map(
          (c) =>
            `| ${c.clauseReference} | ${statusLabelSv(c.status)} | ${statusLabelSv(c.riskLevel)} | ${c.deviationDescription ?? "–"} |`
        )
        .join("\n")
      lines.push(`\n| Klausul | Status | Risk | Avvikelse |\n|---|---|---|---|\n${clauseRows}\n`)
    }
    lines.push("")
  }
  return lines.join("\n")
}

export function renderStep3(bidReview: BidReviewResult): string {
  const lines: string[] = []

  lines.push("### Granskningssummering\n")
  if (bidReview.bidDocumentFilenames.length > 0) {
    lines.push(`**Granskade anbudsfiler:** ${bidReview.bidDocumentFilenames.join(", ")}`)
  }
  if (bidReview.goNoGo) {
    lines.push(`**Rekommendation:** ${statusLabelSv(bidReview.goNoGo)}`)
  }
  if (bidReview.complianceScore != null) {
    lines.push(`**Compliance-poäng:** ${bidReview.complianceScore}/100`)
  }
  if (bidReview.overallAssessment) {
    lines.push(`\n${bidReview.overallAssessment}`)
  }
  if (bidReview.goNoGoReasoning) {
    lines.push(`\n**Motivering:** ${bidReview.goNoGoReasoning}`)
  }

  if (bidReview.findings.length > 0) {
    lines.push("\n### Fynd\n")
    lines.push(renderFindingGroups(bidReview.findings))
  }

  if (bidReview.criterionScores && bidReview.criterionScores.length > 0) {
    lines.push("\n### Poängsättning per kriterium\n")
    const rows = bidReview.criterionScores
      .map(
        (c) =>
          `| ${c.criterionId} | ${c.criterionName} | ${c.weightPercent}% | ${c.predictedScore}/${c.maxScore} |`
      )
      .join("\n")
    lines.push(`| ID | Kriterium | Vikt | Förväntat poäng |\n|---|---|---|---|\n${rows}\n`)
    if (bidReview.totalPredictedScore != null && bidReview.totalMaxScore != null) {
      lines.push(`\n**Totalt:** ${bidReview.totalPredictedScore}/${bidReview.totalMaxScore}`)
    }
    if (bidReview.scoringNotes) lines.push(`\n*${bidReview.scoringNotes}*`)
    for (const c of bidReview.criterionScores) {
      if (c.improvementSuggestions.length > 0) {
        lines.push(`\n**${c.criterionId} — förbättringsförslag:**`)
        for (const s of c.improvementSuggestions) lines.push(`- ${s}`)
      }
    }
  }

  if (bidReview.requirementAnalyses?.length) {
    lines.push("\n### Kravuppfyllnad per krav\n")
    const rows = bidReview.requirementAnalyses
      .map(
        (a) =>
          `| ${a.requirementId} | ${a.description.substring(0, 50)}${a.description.length > 50 ? "…" : ""} | ${a.isMandatory ? "Obligatorisk" : "Bör"} | ${statusLabelSv(a.status)} | ${Math.round(a.confidence * 100)}% |`
      )
      .join("\n")
    lines.push(`| ID | Krav | Typ | Status | Säkerhet |\n|---|---|---|---|---|\n${rows}\n`)
    for (const a of bidReview.requirementAnalyses) {
      if (a.bidQuote || a.gapDescription || a.suggestedFix) {
        lines.push(`\n#### ${a.requirementId}`)
        if (a.bidQuote) lines.push(`\n**Anbudets formulering:** "${a.bidQuote}"`)
        if (a.gapDescription) lines.push(`\n**Gap:** ${a.gapDescription}`)
        if (a.suggestedFix) lines.push(`\n**Förslag:** ${a.suggestedFix}`)
      }
    }
  } else if (bidReview.requirementStatuses.length > 0) {
    lines.push("\n### Kravstatus\n")
    const rows = bidReview.requirementStatuses
      .map(
        (r) =>
          `| ${r.requirementId} | ${r.requirementDescription.substring(0, 60)}${r.requirementDescription.length > 60 ? "…" : ""} | ${statusLabelSv(r.status)} | ${r.note ?? "–"} |`
      )
      .join("\n")
    lines.push(`| ID | Krav | Status | Notering |\n|---|---|---|---|\n${rows}\n`)
  }

  if (bidReview.contractAnalyses?.length) {
    lines.push(renderContractAnalyses(bidReview.contractAnalyses))
  }
  if (bidReview.timelineChecks?.length) {
    lines.push(renderTimelineChecks(bidReview.timelineChecks))
  }
  if (bidReview.frameworkAnalyses?.length) {
    lines.push(renderFrameworkAnalyses(bidReview.frameworkAnalyses))
  }

  if (bidReview.priorityItems?.length) {
    lines.push("\n### Prioriterade åtgärder\n")
    for (const p of bidReview.priorityItems) {
      lines.push(
        `${p.rank}. **[${p.impact.toUpperCase()}] ${p.title}** *(${p.category})*: ${p.description}`
      )
    }
  }

  if (bidReview.strengthsSummary?.length) {
    lines.push("\n### Styrkor\n")
    for (const s of bidReview.strengthsSummary) lines.push(`- ${s}`)
  }

  if (bidReview.competitiveNotes) {
    lines.push(`\n**Konkurrensbild:** ${bidReview.competitiveNotes}`)
  }

  return lines.join("\n")
}
